import { z } from 'zod';
import type { RevisionScheme } from './scheme';

/** The revision paper colors in the order a production moves through them. */
export const REVISION_COLORS = [
  'white',
  'blue',
  'pink',
  'yellow',
  'green',
  'goldenrod',
  'buff',
  'salmon',
  'cherry',
  'tan',
  'lavender',
] as const;
export type RevisionColor = (typeof REVISION_COLORS)[number];

/** Each pass through the colors goes up a tier: white, …, then double white, …, up to quadruple lavender. */
export const REVISION_TIERS = ['single', 'double', 'triple', 'quadruple'] as const;
export type RevisionTier = (typeof REVISION_TIERS)[number];

type Key<T extends RevisionTier, C extends RevisionColor> = T extends 'single' ? C : `${T}_${C}`;
export type RevisionColorKey = Key<RevisionTier, RevisionColor>;

/**
 * The 44 color keys in canonical order: every single color, then every double, and so on. The order holds whether a
 * production goes double after goldenrod or after lavender, since any double sorts above any single. SQL's
 * `revision_color_keys()` holds the same list (a test reads the migration).
 */
export const REVISION_COLOR_KEYS = REVISION_TIERS.flatMap((tier) =>
  REVISION_COLORS.map((color) => (tier === 'single' ? color : `${tier}_${color}`)),
) as RevisionColorKey[];

export const revisionColorSchema = z.enum(
  REVISION_COLOR_KEYS as [RevisionColorKey, ...RevisionColorKey[]],
);

/** `pages`: only the changed pages; `collated`: the whole script, each page marked with its own color. */
export const versionFormatSchema = z.enum(['pages', 'collated']);
export type VersionFormat = z.infer<typeof versionFormatSchema>;

/** tier × 11 + position: the key's place in the canonical order. */
export function revisionColorRank(key: RevisionColorKey): number {
  return REVISION_COLOR_KEYS.indexOf(key);
}

/** The color a key names, without its tier: what the design token is keyed by. */
export function revisionColorBase(key: RevisionColorKey): RevisionColor {
  return REVISION_COLORS[revisionColorRank(key) % REVISION_COLORS.length] as RevisionColor;
}

export function revisionColorTier(key: RevisionColorKey): RevisionTier {
  return REVISION_TIERS[
    Math.floor(revisionColorRank(key) / REVISION_COLORS.length)
  ] as RevisionTier;
}

export function sortRevisionColors(keys: Iterable<RevisionColorKey>): RevisionColorKey[] {
  return [...new Set(keys)].sort((a, b) => revisionColorRank(a) - revisionColorRank(b));
}

/** The colors of a version that no earlier version has: the ones a run analyses. */
export function newColorsFor(
  colors: Iterable<RevisionColorKey>,
  earlier: Iterable<RevisionColorKey>,
): RevisionColorKey[] {
  const seen = new Set(earlier);
  return sortRevisionColors(colors).filter((c) => !seen.has(c));
}

/** The six-color cycle some productions use: white … goldenrod, then double white. */
const SHORT_CYCLE = 6;

function position(key: RevisionColorKey): number {
  return revisionColorRank(key) % REVISION_COLORS.length;
}

/**
 * The colors missing below the highest seen. A color counts as missed only when it is missing under the six-color
 * cycle too, so goldenrod then double white is no gap; once a color past goldenrod is seen, the production uses all
 * eleven and the full order applies.
 */
function missingBelow(seen: Set<RevisionColorKey>): RevisionColorKey[] {
  if (seen.size === 0) return [];
  const highest = Math.max(...[...seen].map(revisionColorRank));
  const shortCycle = [...seen].every((k) => position(k) < SHORT_CYCLE);
  return REVISION_COLOR_KEYS.slice(0, highest).filter(
    (k) => !seen.has(k) && (!shortCycle || position(k) < SHORT_CYCLE),
  );
}

/** A version as the scopes view gives it: only `color` versions count for the missed-colors rule. */
export type VersionScope = {
  versionId: string;
  scheme: RevisionScheme;
  format: VersionFormat;
  labels: (string | null)[];
};

function colorsOf(version: VersionScope): RevisionColorKey[] {
  if (version.scheme !== 'color') return [];
  return version.labels.flatMap((l) => {
    const key = revisionColorSchema.safeParse(l);
    return key.success ? [key.data] : [];
  });
}

/**
 * For versions in order, the colors each `pages` version seems to have skipped: missing from everything received up
 * to it, and from every later version too (a later version that has the color clears the flag). A collated version
 * holds the whole script, so it never skips one. A version with no colors yet, and a `custom` or `changed` version,
 * is left out of the count: it is neither flagged nor counted as seen.
 */
export function missedRevisionColors(
  versions: VersionScope[],
): { versionId: string; missing: RevisionColorKey[] }[] {
  const seen = new Set<RevisionColorKey>();
  return versions.map((version, i) => {
    const colors = colorsOf(version);
    for (const c of colors) seen.add(c);
    if (version.format !== 'pages' || colors.length === 0)
      return { versionId: version.versionId, missing: [] };
    const later = new Set(versions.slice(i + 1).flatMap(colorsOf));
    return {
      versionId: version.versionId,
      missing: missingBelow(seen).filter((c) => !later.has(c)),
    };
  });
}
